import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonCopyText } from '@components/atoms/button-copy-text';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Link } from '@components/atoms/link';
import { Swiper } from '@components/atoms/swiper';
import { Typography } from '@components/atoms/typography';
import { CountryFlag } from '@components/moleculs/country-flag';
import { LabeledField } from '@components/moleculs/labeled-field';
import { TextField } from '@components/moleculs/text-field';
import { usePolicyLinks } from '@hooks/domain';
import { useCountryOptions } from '@hooks/options/use-country-options';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import { DocsVerificationStepEnum } from '@models/enums';
import { NotCorrectVerificationBanner } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps/start/not-correct-verification-banner';
import { Header } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/header';
import { useCameraConstraints } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-camera-constraints';
import { useCustomPartnerFetchStatus } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-custom-partner-fetch-status';
import { useVerificationUrl } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-verification-url';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { RequirementTypeEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/requirements/types';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '@store';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { useAppTheme } from '@theme';
import { getImageUrl } from '@utils/get-image-url';
import { SwiperSlide } from 'swiper/react';

import { useSelectedChecks } from '../../hooks/use-selected-checks';
import { QrLink } from '../../qr-link';
import { Requirements } from '../../requirements';
import { RequirementsButton } from '../styles';

import { useStartConsent } from './consent-block/use-start-consent';
import { Card } from './card';
import { StartConfirmation } from './start-confirmation';
import { CardsContainer, StartStepContainer } from './styles';
import { Timer } from './timer';

export const StartStep: React.FC = () => {
    const { scrollTop } = useWidgetContext();
    const { t } = useTranslation();
    const theme = useAppTheme();
    const dispatch = useAppDispatch();
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const isTermsAccepted = useAppSelector((state) => state.docsVerification.isTermsAccepted);
    const { verificationUrl } = useVerificationUrl();
    const isCustomPartner = useAppSelector(
        (state) => state.docsVerification.sdkOptions?.customPartner?.concordium,
    );

    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const matchesMobileMd = useMediaQuery(`(max-width:${theme.breakPoints.md})`);
    const { height } = useScreenFullHeight();
    const [selectedCardRequirements, setSelectedCardRequirements] =
        useState<RequirementTypeEnum | null>(null);

    const { isHasProofAddress, isHasPersonalData, isHasLiveness, isHasPoI } = useSelectedChecks();
    const [isTermsConfirmation, setIsTermsConfirmation] = useState<boolean>(() => isTermsAccepted);
    const { cameraConstraints } = useCameraConstraints();
    const [isLoading, setIsLoading] = useState(false);
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);

    const countryOptions = useCountryOptions();
    const consent = useStartConsent();
    const [consentSignError, setConsentSignError] = useState<string>();

    const handleStartVerificationClock = async () => {
        const goToVerificationStep = () => {
            if (sdkOptions) {
                dispatch(docsVerificationSlice.actions.setIsTermsAccepted(true));
                dispatch(
                    docsVerificationSlice.actions.setCurrentStep(
                        DocsVerificationStepEnum.VERIFICATION,
                    ),
                );
                setTimeout(() => {
                    scrollTop();
                });
            }
        };

        try {
            setIsLoading(true);
            setConsentSignError(undefined);

            try {
                await consent.sign();
            } catch {
                setConsentSignError(
                    t('docVerificationWidget.consent.signError', {
                        defaultValue: 'Could not save your consent. Please try again.',
                    }),
                );

                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints);

            const tracks = stream.getTracks();

            tracks?.forEach((track) => {
                track.stop();
            });
            goToVerificationStep();
        } catch {
            // does not allow to go further if allow_poi_manual_uploads is false
            if (sdkOptions?.settings.allow_poi_manual_uploads) {
                goToVerificationStep();
            } else {
                setIsCameraNotAllowed(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsTermsConfirmation(e.target.checked);
    };

    const sdkCountry = countryOptions.find((opt) => opt.value === sdkOptions?.country)?.label || '';

    const handleRequirementsClick = useCallback(
        (slide: number) => {
            setSelectedCardRequirements(slide);
            setTimeout(() => {
                scrollTop();
            });
        },
        [scrollTop],
    );

    const isNotCorrectVerification = useMemo(
        () => ![isHasProofAddress, isHasLiveness, isHasPoI].filter((item) => !!item).length,
        [isHasProofAddress, isHasLiveness, isHasPoI],
    );

    const cards = useMemo(() => {
        let number = 0;

        const result = [];

        if (isHasPersonalData) {
            number += 1;
            result.push({
                icon: getImageUrl('/images/sdk/verification/complete-personal-data.png'),
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>{t('docVerificationWidget.start.completePersonalData')}</span>
                    </div>
                ),
            });
        }

        if (isHasPoI) {
            number += 1;
            result.push({
                icon: getImageUrl('/images/sdk/verification/passport.png'),
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>
                            {t('docVerificationWidget.start.prepareIdentityDocumentMobile')}
                        </span>
                        <RequirementsButton
                            variant='ghost'
                            onClick={() => {
                                handleRequirementsClick(RequirementTypeEnum.PASSPORT);
                            }}
                        >
                            {t('common.requirements')}
                        </RequirementsButton>
                    </div>
                ),
            });
        }

        if (isHasLiveness) {
            number += 1;
            result.push({
                icon: getImageUrl('/images/sdk/verification/selfie.png'),
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>{t('docVerificationWidget.start.prepareSelfie')}</span>
                        <RequirementsButton
                            variant='ghost'
                            onClick={() => {
                                handleRequirementsClick(RequirementTypeEnum.SELFIE);
                            }}
                        >
                            {t('common.requirements')}
                        </RequirementsButton>
                    </div>
                ),
            });
        }

        if (isHasProofAddress) {
            number += 1;
            result.push({
                icon: getImageUrl('/images/sdk/verification/poa.png'),
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>{t('docVerificationWidget.start.prepareUtilityBill')}</span>
                        <RequirementsButton
                            variant='ghost'
                            onClick={() => {
                                handleRequirementsClick(RequirementTypeEnum.POA);
                            }}
                        >
                            {t('common.requirements')}
                        </RequirementsButton>
                    </div>
                ),
            });
        }

        return result;
    }, [t, handleRequirementsClick, isHasPersonalData, isHasPoI, isHasProofAddress, isHasLiveness]);

    const handleBackClick = () => {
        setSelectedCardRequirements(null);
    };

    const { termsLink, privacyLink } = usePolicyLinks();

    const termsLabel = (
        <Trans
            i18nKey='docVerificationWidget.start.terms'
            components={{
                link1: <Link href={termsLink || ''} target='_blank' rel='noreferrer' />,
                link2: <Link href={privacyLink || ''} target='_blank' rel='noreferrer' />,
            }}
        />
    );

    const link = sdkOptions?.isDemo ? window.location.href : verificationUrl || '';

    useCustomPartnerFetchStatus();

    return (
        <StartStepContainer>
            <Box
                className='content-wrapper'
                style={{ height: matchesMobileSm && height ? height - 48 : undefined }}
            >
                {isCameraNotAllowed ? (
                    <CameraAccessDefault
                        onClose={() => {
                            setIsCameraNotAllowed(false);
                        }}
                    />
                ) : (
                    <React.Fragment>
                        {selectedCardRequirements === null ? (
                            <div className='main-content-container'>
                                <div className='logo-container'>
                                    <Header />
                                </div>
                                <Typography
                                    tag='h3'
                                    variant='h2'
                                    color='text-main'
                                    className='title'
                                    data-testid='docver-widget-start-title'
                                >
                                    {t('docVerificationWidget.start.title')}
                                </Typography>
                                <Timer />

                                {isNotCorrectVerification ? (
                                    <NotCorrectVerificationBanner />
                                ) : (
                                    <React.Fragment>
                                        {matchesMobileMd ? (
                                            <Swiper
                                                autoplay={false}
                                                indicatorVariant='dots'
                                                className='carousel'
                                            >
                                                {cards.map((card) => (
                                                    <SwiperSlide key={card.number}>
                                                        <Card {...card} />
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        ) : (
                                            <CardsContainer className='cards-container'>
                                                {cards.map((card) => (
                                                    <Card key={card.number} {...card} />
                                                ))}
                                            </CardsContainer>
                                        )}
                                    </React.Fragment>
                                )}

                                {matchesMobileMd ? null : (
                                    <div className='links-container'>
                                        <QrLink link={link} />
                                        <div className='card'>
                                            <LabeledField
                                                label={t('common.copyLink')}
                                                className='copy-link-title'
                                            >
                                                <TextField
                                                    disabled={true}
                                                    fullWidth={true}
                                                    value={link}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <ButtonCopyText
                                                                copyText={verificationUrl || ''}
                                                                tooltipProps={{
                                                                    title: t('common.copied'),
                                                                    placement: 'bottom',
                                                                }}
                                                                className='icon'
                                                            />
                                                        ),
                                                    }}
                                                    inputSize='m'
                                                    sx={{
                                                        '.MuiInputBase-root input.MuiInputBase-input.Mui-disabled':
                                                            {
                                                                opacity: 1,
                                                                color: 'primaryA1',
                                                            },
                                                    }}
                                                />
                                            </LabeledField>
                                        </div>
                                    </div>
                                )}
                                <div className='footer-container'>
                                    {sdkOptions?.country && (
                                        <LabeledField
                                            label={t('docVerificationWidget.start.selectedCountry')}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                <CountryFlag name={sdkOptions.country} />
                                                <span>{sdkCountry}</span>
                                            </Box>
                                        </LabeledField>
                                    )}
                                    {!isNotCorrectVerification && (
                                        <StartConfirmation
                                            consent={consent}
                                            consentSignError={consentSignError}
                                            termsLabel={termsLabel}
                                            isTermsConfirmation={isTermsConfirmation}
                                            onTermsChange={handleTermsChange}
                                            isCustomPartner={isCustomPartner}
                                            isSkipIntro={sdkOptions?.isSkipIntro}
                                            isLoading={isLoading}
                                            onStart={handleStartVerificationClock}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <Requirements
                                type={selectedCardRequirements}
                                onBack={handleBackClick}
                            />
                        )}
                    </React.Fragment>
                )}
            </Box>
        </StartStepContainer>
    );
};
